import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useClub } from '../../app/club';
import { useRepo } from '../../app/services';
import { useAccessRules, useClubMutation, useGroupMembers, useGroups } from '../../data/hooks';
import { Button, Dialog, EmptyState, ErrorState, Field, Loading, PageHeader, Panel, useToast } from '../../components/ui';
import { ExpiryChoiceField } from '../../components/ExpiryChoiceField';
import { computeScope, inScope } from '../../app/scope';
import { can } from '../../lib/roles';
import { fmtDate } from '../../lib/format';
import { resolveExpiry, UNSET_EXPIRY, type ExpiryChoice } from '../../lib/expiry';
import type { Group } from '../../lib/types';
import { errorText } from '../../lib/errors';

export function GroupsPage() {
  const { orgId, role, managedGroupIds, readOnly } = useClub();
  const groups = useGroups(orgId);
  const members = useGroupMembers(orgId);
  const rules = useAccessRules(orgId);
  const [creating, setCreating] = useState(false);

  const qs = [groups, members, rules];
  if (qs.some((q) => q.isLoading)) return <Loading />;
  const err = qs.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;
  const scope = computeScope(role, managedGroupIds, members.data!, rules.data!);
  const rows = groups.data!.filter((g) => inScope(scope, 'groupIds', g.id)).sort((a, b) => a.name.localeCompare(b.name, 'nl'));

  return (
    <div className="stack-lg">
      <PageHeader
        title="Groepen"
        intro="Een groep geeft zijn leden toegang tot deuren op vaste dagen en tijden."
        actions={
          can('group.create', role) && !readOnly && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              Groep maken
            </Button>
          )
        }
      />
      <Panel>
        {rows.length === 0 ? (
          <EmptyState title="Nog geen groepen">
            {role === 'group_manager' ? 'Je beheert nog geen groep. Vraag je clubbeheerder om je als groepsbeheerder toe te voegen.' : 'Maak een groep, bijvoorbeeld "Barvrijwilligers", en geef die toegang tot deuren.'}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="data stack-mobile">
              <thead>
                <tr>
                  <th scope="col">Groep</th>
                  <th scope="col">Seizoen</th>
                  <th scope="col">Leden</th>
                  <th scope="col">Deuren</th>
                  <th scope="col">Einddatum</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => (
                  <tr key={g.id}>
                    <td className="cell-main">
                      <Link className="cell-title" to={`/c/${orgId}/groepen/${g.id}`}>
                        {g.name}
                      </Link>
                    </td>
                    <td data-label="Seizoen">{g.season_label ?? '—'}</td>
                    <td data-label="Leden" className="num">
                      {members.data!.filter((m) => m.group_id === g.id).length}
                    </td>
                    <td data-label="Deuren" className="num">
                      {rules.data!.filter((r) => r.group_id === g.id).length}
                    </td>
                    <td data-label="Einddatum">{g.valid_until ? fmtDate(g.valid_until) : 'Geen'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {creating && <GroupFormDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

const groupSchema = z.object({
  name: z.string().trim().min(2, 'Geef de groep een naam.'),
  season_label: z.string().trim().max(20, 'Maximaal 20 tekens.'),
});

/** Create a group, or duplicate one to the next season (spec 04 user story 5). */
export function GroupFormDialog({ onClose, source, sourceMembers = [] }: { onClose: () => void; source?: Group; sourceMembers?: { id: string; name: string }[] }) {
  const { orgId } = useClub();
  const repo = useRepo();
  const toast = useToast();
  const navigate = useNavigate();
  const nextSeason = (label: string | null) => {
    const m = label && /^(\d{4})\/(\d{2})$/.exec(label);
    return m ? `${Number(m[1]) + 1}/${String((Number(m[2]) + 1) % 100).padStart(2, '0')}` : '';
  };
  const [name, setName] = useState(source ? source.name.replace(/\d{4}\/\d{2}/, nextSeason(source.season_label) || '$&') : '');
  const [season, setSeason] = useState(source ? nextSeason(source.season_label) : '');
  const [expiry, setExpiry] = useState<ExpiryChoice>(UNSET_EXPIRY);
  const [copyIds, setCopyIds] = useState<string[]>([]);
  const [memberExpiry, setMemberExpiry] = useState<ExpiryChoice>(UNSET_EXPIRY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useClubMutation(orgId, (input: { name: string; season_label: string | null; valid_until: string | null; member_valid_until: string | null }) =>
    source
      ? repo.duplicateGroup(source.id, { ...input, copy_member_ids: copyIds })
      : repo.createGroup(orgId, input),
  );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const parsed = groupSchema.safeParse({ name, season_label: season });
    if (!parsed.success) for (const i of parsed.error.issues) errs[String(i.path[0])] = i.message;
    const exp = resolveExpiry(expiry);
    if (!exp.ok) errs.expiry = exp.error;
    let memberUntil: string | null = null;
    if (source && copyIds.length) {
      const groupUntil = exp.ok ? exp.validUntil : null;
      const m = resolveExpiry(memberExpiry, { groupValidUntil: groupUntil });
      if (!m.ok) errs.memberExpiry = m.error;
      else memberUntil = m.validUntil;
    }
    setErrors(errs);
    if (Object.keys(errs).length || !exp.ok) return;
    try {
      const g = await save.mutateAsync({ name: name.trim(), season_label: season.trim() || null, valid_until: exp.validUntil, member_valid_until: memberUntil });
      toast(source ? `${g.name} aangemaakt. De oude groep blijft actief tot je hem archiveert.` : `${g.name} aangemaakt`);
      onClose();
      navigate(`/c/${orgId}/groepen/${g.id}`);
    } catch (err) {
      setErrors({ form: errorText(err, 'Opslaan mislukt') });
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      wide={!!source}
      title={source ? 'Groep dupliceren naar volgend seizoen' : 'Groep maken'}
      description={source ? 'Deuren en tijden worden overgenomen. Kies welke leden meegaan.' : 'Daarna stel je in tot welke deuren de groep toegang heeft.'}
    >
      <form className="stack" onSubmit={submit} noValidate>
        <Field label="Naam" error={errors.name}>
          {(p) => <input {...p} className="input" value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label="Seizoen (optioneel)" hint="Bijvoorbeeld 2026/27" error={errors.season_label}>
          {(p) => <input {...p} className="input" value={season} onChange={(e) => setSeason(e.target.value)} style={{ maxWidth: 200 }} />}
        </Field>
        <ExpiryChoiceField legend="Einddatum groep" value={expiry} onChange={setExpiry} error={errors.expiry} />
        {source && (
          <fieldset className="field">
            <legend>Leden meenemen</legend>
            <div className="row">
              <Button size="sm" onClick={() => setCopyIds(sourceMembers.map((m) => m.id))}>
                Iedereen
              </Button>
              <Button size="sm" onClick={() => setCopyIds([])}>
                Niemand
              </Button>
              <span className="muted small">{copyIds.length} van {sourceMembers.length} gekozen</span>
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 4, padding: '6px 12px' }}>
              {sourceMembers.map((m) => (
                <label key={m.id} className="row" style={{ gap: 8, padding: '4px 0' }}>
                  <input
                    type="checkbox"
                    checked={copyIds.includes(m.id)}
                    onChange={(e) => setCopyIds((ids) => (e.target.checked ? [...ids, m.id] : ids.filter((x) => x !== m.id)))}
                    style={{ width: 18, height: 18 }}
                  />
                  {m.name}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {source && copyIds.length > 0 && (
          <ExpiryChoiceField legend="Einddatum voor de meegenomen leden" value={memberExpiry} onChange={setMemberExpiry} error={errors.memberExpiry} />
        )}
        {errors.form && (
          <div className="alert alert-danger" role="alert">
            {errors.form}
          </div>
        )}
        <div className="dialog-actions">
          <Button onClick={onClose}>Annuleren</Button>
          <Button variant="primary" type="submit" disabled={save.isPending}>
            {source ? 'Dupliceren' : 'Groep maken'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
